import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { ComponentType, PointerEvent as ReactPointerEvent } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import svg from "../../utils/svg"
import { formatCurrency } from "../../utils/utils"
import styles from "./EnclavePaywall.module.scss"

// ─────────────────────────────────────────────────────────────────────────────
// "Enclave" — a NON-ONBOARDING premium upgrade, shown to a free user who already
// keeps memories in the vault. It is built on a single behavioural spine:
// COMMITMENT + PROGRESS-COMPLETION. The whole screen is one unfinished action —
// an open circle around your most personal photos — and the user finishes it
// with one deliberate gesture: they *slide to seal*. As the seal is drawn, a
// ring of light closes around a scattered pile of real memories, the photos
// gather inward, and each real Premium protection ignites on the ring in turn.
// Completing the arc is the purchase. You don't "buy a subscription" — you
// close the circle around what's yours.
//
// Nothing here is lifted from another paywall. The mechanic (a horizontal
// seal-drag driving a drawn ring + node ignition), the layout (a centred radial
// stage rather than hero→bullets→CTA), the metaphor (encircling / sealing a
// private enclave), and the emotional register (warm candlelight resolving into
// a calm, secured blue) are all new. Only the house fonts, colour tokens and
// existing feature icons are reused.
//
// Yearly is the hero: the largest, glowing plan, default-selected, with the
// per-month breakout, trial and savings; monthly is deliberately small and
// secondary. The seal always confirms the *selected* plan. Copy is App Store
// compliant — sealing turns on Premium protections; it never implies photos are
// lost, held hostage, or removed when a subscription ends.
// ─────────────────────────────────────────────────────────────────────────────

const PUB = process.env.PUBLIC_URL ?? ""
const mem = (n: number) => `${PUB}/enclave/mem${n}.jpg`

// The scattered pile of personal memories inside the ring. Each tile carries a
// resting position (%), a rotation, and a "gather" vector (px) it drifts along
// as the seal closes — so the loose pile visibly draws together into the enclave.
type Tile = {
  n: number
  x: number
  y: number
  s: number
  rot: number
  gx: number
  gy: number
  z: number
  d: number
}
const TILES: Tile[] = [
  { n: 2, x: 50, y: 32, s: 104, rot: -4, gx: 0, gy: 12, z: 6, d: 0 },
  { n: 4, x: 27, y: 43, s: 88, rot: -10, gx: 20, gy: 6, z: 5, d: 0.5 },
  { n: 1, x: 73, y: 45, s: 90, rot: 9, gx: -20, gy: 4, z: 5, d: 0.9 },
  { n: 7, x: 39, y: 63, s: 82, rot: 7, gx: 13, gy: -16, z: 4, d: 0.3 },
  { n: 6, x: 63, y: 65, s: 80, rot: -8, gx: -13, gy: -16, z: 4, d: 1.2 },
  { n: 3, x: 22, y: 66, s: 64, rot: 11, gx: 24, gy: -18, z: 3, d: 0.7 },
  { n: 5, x: 79, y: 67, s: 64, rot: -12, gx: -24, gy: -18, z: 3, d: 1.5 },
]

// The six real Premium protections, placed as nodes around the ring. Each
// ignites the moment the seal sweeps past its position (a fraction of the arc).
type Node = {
  key: string
  Icon: ComponentType<{ className?: string }>
  label: string
  say: string
}
const NODES: Node[] = [
  {
    key: "backup",
    Icon: svg.featureIcons.CloudVault,
    label: "Encrypted backup",
    say: "A private, end-to-end encrypted second copy",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    label: "Break-in alerts",
    say: "A silent photo of anyone who guesses wrong",
  },
  {
    key: "decoy",
    Icon: svg.featureIcons.Decoy,
    label: "Decoy vault",
    say: "A believable second vault for prying eyes",
  },
  {
    key: "disguise",
    Icon: svg.featureIcons.Disguise,
    label: "App disguise",
    say: "Hide the app behind an everyday icon",
  },
  {
    key: "devices",
    Icon: svg.featureIcons.Wifi,
    label: "Every device",
    say: "Your enclave, in sync everywhere you sign in",
  },
  {
    key: "recover",
    Icon: svg.featureIcons.Trash,
    label: "Recover deleted",
    say: "Undo any delete, long after it's gone",
  },
]

// Fraction of the arc at which each node ignites (evenly spaced, offset so the
// first lights almost immediately and the last lands right at the seal).
const nodeAt = (i: number) => (i + 0.7) / NODES.length

const RING = 150 // svg radius
const CIRC = 2 * Math.PI * RING
const COMPLETE = 0.94 // release past this = sealed

function EnclavePaywall() {
  const { state } = useAppState()
  const reduce = useReducedMotion()

  // Respect the device safe-area insets. The root is full-bleed (position:
  // fixed), so without this the home-indicator footer overlay would sit on top
  // of the seal slider and swallow the gesture.
  const topInset = state.initialData?.topInset ?? 0
  const bottomInset = state.initialData?.bottomInset ?? 0

  const premium = state.productData?.premium
  const yearly = premium?.yearlyProduct
  const monthly = premium?.monthlyProduct
  const locale = yearly?.priceLocale

  const yearlyPrice = yearly ? formatCurrency(yearly.price, locale) : ""
  const perMonth = yearly ? formatCurrency(yearly.price / 12, locale) : ""
  const monthlyPrice = monthly ? formatCurrency(monthly.price, locale) : ""
  const trialDays = yearly?.introPricing?.period ?? 7

  const savings = useMemo(() => {
    if (!yearly || !monthly) return 0
    return Math.round((1 - yearly.price / (monthly.price * 12)) * 100)
  }, [yearly, monthly])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const [progress, setProgress] = useState(0)
  const [sealed, setSealed] = useState(false)
  const [showClose, setShowClose] = useState(false)

  const trackRef = useRef<HTMLDivElement | null>(null)
  const draggingRef = useRef(false)
  const rafBack = useRef<number | undefined>(undefined)
  const sealedRef = useRef(false)

  // Reveal the dismiss control after a beat — long enough for the ring + first
  // protections to read, short enough never to feel like a trap.
  useEffect(() => {
    const t = window.setTimeout(() => setShowClose(true), reduce ? 0 : 900)
    return () => window.clearTimeout(t)
  }, [reduce])

  const activeCount = NODES.filter((_, i) => progress >= nodeAt(i)).length
  const lastSay =
    activeCount > 0 ? NODES[Math.min(activeCount, NODES.length) - 1].say : null

  const finishSeal = useCallback(() => {
    if (sealedRef.current) return
    sealedRef.current = true
    setSealed(true)
    setProgress(1)
    const product = plan === "yearly" ? yearly : monthly
    window.setTimeout(
      () => {
        if (product) native.subscribe(product)
      },
      reduce ? 0 : 900,
    )
  }, [plan, yearly, monthly, reduce])

  const setFromClientX = useCallback((clientX: number) => {
    const el = trackRef.current
    if (!el) return 0
    const rect = el.getBoundingClientRect()
    const pad = rect.height * 0.5 // thumb radius ≈ half track height
    const usable = rect.width - pad * 2
    const p = (clientX - rect.left - pad) / Math.max(1, usable)
    const clamped = Math.max(0, Math.min(1, p))
    setProgress(clamped)
    return clamped
  }, [])

  const onPointerDown = (e: ReactPointerEvent) => {
    if (sealedRef.current) return
    if (rafBack.current) cancelAnimationFrame(rafBack.current)
    draggingRef.current = true
    ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
    setFromClientX(e.clientX)
  }

  const onPointerMove = (e: ReactPointerEvent) => {
    if (!draggingRef.current) return
    setFromClientX(e.clientX)
  }

  // Ease the thumb back to the start when released short of the seal.
  const springBack = useCallback((from: number) => {
    const start = from
    let t0: number | null = null
    const dur = 340
    const step = (now: number) => {
      if (t0 == null) t0 = now
      const k = Math.min(1, (now - t0) / dur)
      const eased = 1 - Math.pow(1 - k, 3)
      setProgress(start * (1 - eased))
      if (k < 1) rafBack.current = requestAnimationFrame(step)
    }
    rafBack.current = requestAnimationFrame(step)
  }, [])

  const onPointerUp = (e: ReactPointerEvent) => {
    if (!draggingRef.current) return
    draggingRef.current = false
    const p = setFromClientX(e.clientX)
    if (p >= COMPLETE) {
      finishSeal()
    } else if (reduce) {
      setProgress(0)
    } else {
      springBack(p)
    }
  }

  // Reduced-motion / non-drag fallback: the track is also a plain button.
  const tapToSeal = () => {
    if (reduce) finishSeal()
  }

  useEffect(() => {
    return () => {
      if (rafBack.current) cancelAnimationFrame(rafBack.current)
    }
  }, [])

  const handleClose = () => native.postNativeEvent("close")

  const subLine =
    plan === "yearly"
      ? `${trialDays}-day free trial, then ${yearlyPrice}/year (≈ ${perMonth}/mo). Cancel anytime.`
      : `${monthlyPrice}/month. Cancel anytime.`

  return (
    <div className={`${styles.root} ${sealed ? styles.isSealed : ""}`}>
      {/* Ambient candlelight → resolves cooler once sealed */}
      <div className={styles.ambient} aria-hidden>
        <span className={styles.glowWarm} />
        <span className={styles.glowCool} />
        <span className={styles.grain} />
      </div>

      <div
        className={styles.scroll}
        style={{ paddingTop: topInset, paddingBottom: bottomInset + 12 }}
      >
        {/* Top bar */}
        <div className={styles.topbar}>
          <span className={styles.wordmark}>
            <span className={styles.wmDot} aria-hidden />
            PRIVATE&nbsp;PHOTO&nbsp;VAULT
          </span>
          <button
            className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
            onClick={handleClose}
            aria-label="Not now"
            tabIndex={showClose ? 0 : -1}
          >
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Headline */}
        <header className={styles.hero}>
          <p className={styles.eyebrow}>Enclave · Premium</p>
          <h1 className={styles.title}>
            The moments too personal
            <br />
            <span className={styles.titleGrad}>to leave in the open.</span>
          </h1>
          <p className={styles.sub}>
            Draw the circle closed and your most private photos become an
            enclave — every Premium protection switched on at once.
          </p>
        </header>

        {/* ── The stage: photos + the ring being sealed ─────────────────── */}
        <div
          className={styles.stage}
          style={{ ["--seal" as any]: sealed ? 1 : progress }}
        >
          <div className={styles.ringWrap}>
            <svg className={styles.ringSvg} viewBox="0 0 340 340" aria-hidden>
              <defs>
                <linearGradient id="enc-seal" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stopColor="#ffd9a0" />
                  <stop offset="0.5" stopColor="#ffb27a" />
                  <stop offset="1" stopColor="#6f97ff" />
                </linearGradient>
                <filter
                  id="enc-soft"
                  x="-30%"
                  y="-30%"
                  width="160%"
                  height="160%"
                >
                  <feGaussianBlur stdDeviation="4" />
                </filter>
              </defs>
              {/* faint full guide circle (the open, unfinished shape) */}
              <circle
                cx="170"
                cy="170"
                r={RING}
                fill="none"
                stroke="rgba(255,236,208,0.14)"
                strokeWidth="1.5"
                strokeDasharray="2 7"
              />
              {/* glow underlay of the drawn seal */}
              <circle
                className={styles.sealGlow}
                cx="170"
                cy="170"
                r={RING}
                fill="none"
                stroke="url(#enc-seal)"
                strokeWidth="9"
                strokeLinecap="round"
                strokeDasharray={CIRC}
                strokeDashoffset={CIRC * (1 - (sealed ? 1 : progress))}
                transform="rotate(-90 170 170)"
                filter="url(#enc-soft)"
              />
              {/* the seal stroke itself */}
              <circle
                className={styles.sealLine}
                cx="170"
                cy="170"
                r={RING}
                fill="none"
                stroke="url(#enc-seal)"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeDasharray={CIRC}
                strokeDashoffset={CIRC * (1 - (sealed ? 1 : progress))}
                transform="rotate(-90 170 170)"
              />
            </svg>

            {/* Protection nodes around the ring */}
            {NODES.map((node, i) => {
              const frac = nodeAt(i)
              const angle = -90 + frac * 360
              const rad = (angle * Math.PI) / 180
              const R = 46 // % radius within ringWrap
              const left = 50 + R * Math.cos(rad)
              const top = 50 + R * Math.sin(rad)
              const on = (sealed ? 1 : progress) >= frac
              return (
                <span
                  key={node.key}
                  className={`${styles.node} ${on ? styles.nodeOn : ""}`}
                  style={{
                    left: `${left}%`,
                    top: `${top}%`,
                    ["--d" as any]: `${i * 55}ms`,
                  }}
                >
                  <node.Icon className={styles.nodeIcon} />
                </span>
              )
            })}

            {/* Photo pile */}
            <div className={styles.pile}>
              {TILES.map((t) => (
                <span
                  key={t.n}
                  className={styles.tileSeat}
                  style={{
                    left: `${t.x}%`,
                    top: `${t.y}%`,
                    zIndex: t.z,
                    width: t.s,
                    height: t.s,
                    ["--gx" as any]: `${t.gx}px`,
                    ["--gy" as any]: `${t.gy}px`,
                    ["--rot" as any]: `${t.rot}deg`,
                  }}
                >
                  <span
                    className={styles.tileFloat}
                    style={{ ["--fd" as any]: `${t.d}s` }}
                  >
                    <img src={mem(t.n)} alt="" loading="eager" />
                  </span>
                </span>
              ))}

              {/* the crest that ignites when the enclave is sealed */}
              <span className={styles.crest} aria-hidden>
                <svg viewBox="0 0 48 48">
                  <path
                    className={styles.crestShield}
                    d="M24 4l15 5v11c0 10-6.6 16.8-15 20-8.4-3.2-15-10-15-20V9z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path
                    className={styles.crestCheck}
                    d="M16.5 24.2l5 5 10-11"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </div>

            {/* live status caption */}
            <div className={styles.status} aria-live="polite">
              {sealed ? (
                <span className={styles.statusSealed}>Enclave sealed</span>
              ) : lastSay ? (
                <span key={lastSay} className={styles.statusSay}>
                  {lastSay}
                </span>
              ) : (
                <span className={styles.statusIdle}>
                  {NODES.length} protections ready
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Always-legible legend of what gets turned on */}
        <ul className={styles.legend}>
          {NODES.map((node, i) => (
            <li
              key={node.key}
              className={`${styles.legendItem} ${
                (sealed ? 1 : progress) >= nodeAt(i) ? styles.legendOn : ""
              }`}
            >
              <node.Icon className={styles.legendIcon} />
              {node.label}
            </li>
          ))}
        </ul>

        {/* ── Plans — yearly is the hero ────────────────────────────────── */}
        <section className={styles.plans} aria-label="Choose your plan">
          <button
            className={`${styles.plan} ${styles.planYear} ${
              plan === "yearly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
            disabled={sealed}
          >
            <span className={styles.planGlow} aria-hidden />
            {savings > 0 && (
              <span className={styles.bestTag}>
                Best value · Save {savings}%
              </span>
            )}
            <span className={styles.planTop}>
              <span className={styles.planName}>Yearly</span>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
            </span>
            <span className={styles.priceRow}>
              <span className={styles.priceBig}>{yearlyPrice}</span>
              <span className={styles.pricePer}>/year</span>
            </span>
            <span className={styles.planFoot}>
              just <b>{perMonth}/mo</b> · {trialDays} days free
            </span>
          </button>

          <button
            className={`${styles.plan} ${styles.planMonth} ${
              plan === "monthly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
            disabled={sealed}
          >
            <span className={styles.planTop}>
              <span className={styles.planName}>Monthly</span>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
            </span>
            <span className={styles.priceRowSm}>
              <span className={styles.priceSm}>{monthlyPrice}</span>
              <span className={styles.pricePerSm}>/mo</span>
            </span>
          </button>
        </section>

        {/* ── The seal: slide to complete the circle ────────────────────── */}
        <div className={styles.sealDock}>
          <div
            ref={trackRef}
            className={`${styles.track} ${sealed ? styles.trackDone : ""}`}
            role={reduce ? "button" : "slider"}
            aria-label={
              sealed ? "Enclave sealed" : "Slide to seal your Enclave"
            }
            aria-valuemin={reduce ? undefined : 0}
            aria-valuemax={reduce ? undefined : 100}
            aria-valuenow={reduce ? undefined : Math.round(progress * 100)}
            onClick={tapToSeal}
          >
            <span
              className={styles.trackFill}
              style={{
                width: `calc(30px + ${sealed ? 1 : progress} * (100% - 60px))`,
              }}
              aria-hidden
            />
            <span
              className={styles.trackLabel}
              style={{ opacity: sealed ? 0 : 1 - progress * 1.6 }}
            >
              {reduce ? "Seal your Enclave" : "Slide to seal your Enclave"}
              {!reduce && (
                <svg
                  className={styles.trackChevrons}
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  aria-hidden
                >
                  <path
                    d="M4 7l5 5-5 5M11 7l5 5-5 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>
            <span
              className={`${styles.thumb} ${sealed ? styles.thumbDone : ""}`}
              style={{
                left: `calc(30px + ${sealed ? 1 : progress} * (100% - 60px))`,
                transition: sealed
                  ? "left 0.5s cubic-bezier(0.34,1.4,0.64,1), box-shadow 0.3s ease, background 0.5s ease"
                  : draggingRef.current
                    ? "box-shadow 0.3s ease, background 0.5s ease"
                    : "left 0.32s ease, box-shadow 0.3s ease, background 0.5s ease",
              }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            >
              {sealed ? (
                <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
                  <path
                    d="M5 12.5l4.4 4.4L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="21" height="21" aria-hidden>
                  <path
                    d="M8 11V8a4 4 0 0 1 8 0v3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                  />
                  <rect
                    x="6"
                    y="11"
                    width="12"
                    height="9"
                    rx="2.4"
                    fill="currentColor"
                  />
                  <circle cx="12" cy="15.4" r="1.5" fill="#1a140f" />
                </svg>
              )}
            </span>
          </div>

          <p className={styles.subLine}>{subLine}</p>
          <p className={styles.reassure}>
            Your photos are always yours. Sealing simply turns on your Premium
            protections — cancel anytime and your whole library stays right
            where it is.
          </p>

          <div className={styles.legal}>
            <button onClick={() => native.postNativeEvent("already_purchased")}>
              Restore
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("terms")}>
              Terms
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("privacy")}>
              Privacy
            </button>
          </div>
        </div>
      </div>

      {/* seal burst */}
      {sealed && <span className={styles.burst} aria-hidden />}
    </div>
  )
}

export default EnclavePaywall
